import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, ArrowDown, Clock, ZoomIn, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/Avatar';
import { FollowButton } from './FollowButton';
import { useNavigate } from 'react-router-dom';
import { ImageModal } from './ImageModal';

const categoryColors = {
  outdoors: 'bg-categoryOutdoors-bg text-categoryOutdoors-text border-categoryOutdoors-text/20',
  crafts: 'bg-categoryCrafts-bg text-categoryCrafts-text border-categoryCrafts-text/20',
  cooking: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20',
  painting: 'bg-categoryPainting-bg text-categoryPainting-text border-categoryPainting-text/20',
  social: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20',
  exercise: 'bg-categoryOutdoors-bg text-categoryOutdoors-text border-categoryOutdoors-text/20',
  learning: 'bg-categoryCrafts-bg text-categoryCrafts-text border-categoryCrafts-text/20',
  entertainment: 'bg-categoryPainting-bg text-categoryPainting-text border-categoryPainting-text/20',
  relaxation: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20'
};

export const PostCard = ({ idea, onVote, onEdit, onDelete, user, index, currentUser }) => {
  const navigate = useNavigate();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const handleVote = (voteType) => {
    onVote(idea.id, voteType);
  };

  const handleUsernameClick = (e) => {
    e.stopPropagation();
    navigate(`/user/${idea.username}`);
  };

  const handleCategoryClick = (e) => {
    e.stopPropagation();
    navigate(`/?category=${idea.category}`);
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setIsImageModalOpen(true);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      data-testid={`idea-card-${index}`}
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-4 sm:p-5 mb-3 border border-cardBorder"
    >
      {/* User Info */}
      <div className="flex items-start gap-2 sm:gap-3 mb-3">
        <Avatar username={idea.username} size="md" className="text-sm sm:text-base" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span 
              className="font-semibold text-primaryDark text-xs sm:text-sm truncate cursor-pointer hover:text-primary-dark transition-colors"
              onClick={handleUsernameClick}
            >
              @{idea.username}
            </span>
            <Badge 
              onClick={handleCategoryClick}
              className={`text-[10px] sm:text-xs px-2 py-0.5 border font-semibold rounded-md cursor-pointer hover:opacity-80 transition-opacity ${categoryColors[idea.category] || 'bg-gray-100'}`}
            >
              {idea.category}
            </Badge>
            {idea.community_name && (
              <Badge className="text-[10px] sm:text-xs px-2 py-0.5 bg-primary/20 text-primary-dark border border-primary/30 font-semibold rounded-md flex items-center gap-1">
                <MapPin className="w-2.5 h-2.5" />
                {idea.community_name}
              </Badge>
            )}
            <span className="text-grayMeta text-[10px] sm:text-xs flex items-center gap-1">
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {idea.time_needed}
            </span>
          </div>
        </div>
        <FollowButton 
          userId={idea.user_id} 
          username={idea.username} 
          size="sm"
          currentUser={currentUser}
        />
      </div>

      {/* Content */}
      <div className="mb-3 sm:mb-4">
        <h3 className="font-baloo font-bold text-primaryDark text-base sm:text-lg mb-1 sm:mb-2 leading-tight">
          {idea.title}
        </h3>
        <p className="text-gray text-xs sm:text-sm leading-relaxed">{idea.description}</p>
        
        {/* Image */}
        {idea.image_url && (
          <div 
            onClick={handleImageClick}
            className="mt-3 rounded-lg overflow-hidden border border-cardBorder cursor-pointer group relative"
          >
            <img 
              src={idea.image_url} 
              alt={idea.title}
              className="w-full max-h-80 object-cover transition-transform group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {/* Zoom indicator overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                <ZoomIn className="w-6 h-6 text-primaryDark" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <button
          data-testid={`upvote-btn-${index}`}
          onClick={() => handleVote('up')}
          className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
            idea.user_vote === 'up'
              ? 'bg-voteUp-bg text-voteUp-text'
              : 'bg-voteDefault text-voteDefault-icon hover:bg-voteUp-bg hover:text-voteUp-text'
          }`}
        >
          <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>{idea.upvotes}</span>
        </button>

        <button
          data-testid={`downvote-btn-${index}`}
          onClick={() => handleVote('down')}
          className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
            idea.user_vote === 'down'
              ? 'bg-voteDown-bg text-voteDown-text'
              : 'bg-voteDefault text-voteDefault-icon hover:bg-voteDown-bg hover:text-voteDown-text'
          }`}
        >
          <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
          <span>{idea.downvotes}</span>
        </button>

        <div className="ml-auto text-xs sm:text-sm font-semibold text-grayMeta">
          {idea.upvotes - idea.downvotes} pts
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={idea.image_url}
        onClose={() => setIsImageModalOpen(false)}
      />
    </motion.article>
  );
};
